import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import _ from 'lodash';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import {
  Box,
  Checkbox,
  Typography,
  Pagination,
  Grid,
  IconButton,
} from '@mui/material';
import {
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  useFlexLayout,
  useResizeColumns,
  useGroupBy,
  useExpanded,
  useColumnOrder,
} from '@nuva-org/react-table';
import { useContextMenu } from 'react-contexify';
import SearchBox from '../SearchBox';
import { ContextMenus } from './ContextMenus';
import { ReactComponent as SortButtonArrowIcon } from '../../../assets/SortButtonArrowIcon.svg';

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;
  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);
  return <Checkbox sx={{ padding: 0 }} ref={resolvedRef} {...rest} />;
});
IndeterminateCheckbox.propTypes = {
  indeterminate: PropTypes.bool.isRequired,
};
const ReactMuiTablePaginationActions = (props) => {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handlePageChange = useCallback(
    (event, _page) => {
      onPageChange(event, _page - 1);
    },
    [onPageChange]
  );
  return (
    <Box sx={{ flexShrink: 0, marginLeft: (theme) => theme.spacing(2.5) }}>
      <Pagination
        count={
          count <= rowsPerPage && count / rowsPerPage === 0
            ? count / rowsPerPage
            : Math.max(0, Math.ceil(count / rowsPerPage))
        }
        page={page + 1}
        shape="rounded"
        onChange={handlePageChange}
        color="primary"
        size="small"
        boundaryCount={3}
      />
    </Box>
  );
};
ReactMuiTablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const RowSelectionHeader = ({
  getToggleAllRowsSelectedProps,
  rowSelectionConfig,
}) => {
  return (
    <Box display="flex" alignItems="center" textAlign="center">
      {rowSelectionConfig.type === 'multi-select' ? (
        <>
          <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          {rowSelectionConfig.showSelectAllLabel ? (
            <Typography color="inherit">Select All</Typography>
          ) : null}
        </>
      ) : (
        <Typography
          variant="body3"
          sx={{ fontWeight: 700 }}
          ml={1}
          color="inherit"
        >
          SELECT
        </Typography>
      )}
    </Box>
  );
};
RowSelectionHeader.propTypes = {
  getToggleAllRowsSelectedProps: PropTypes.func.isRequired,
  rowSelectionConfig: PropTypes.shape({
    type: PropTypes.string.isRequired,
    showSelectAllLabel: PropTypes.bool.isRequired,
  }).isRequired,
};

const ROW_LEVEL_HOVER_OPTIONS_DEFAULT_STYLE = {
  position: 'absolute',
  float: 'right',
  right: 0,
  height: 'inherit',
  display: 'none',
};

const defaultPropGetter = () => ({});

const commonFocusStyle = {
  border: '2px solid !important',
  borderColor: 'primary.main !important',
};

const ReactMuiTableListView = ({
  columns,
  data,
  getHeaderProps = defaultPropGetter,
  getColumnProps = defaultPropGetter,
  getRowProps = defaultPropGetter,
  rowLevelOnHoverOptions,
  externalCustomTheme,
  rowSelectionConfig,
  onTableStateChange,
  enableRowSelection,
  toggle,
  enablePagination = false,
  isAllOptionDisabled = false,
  rowsPerPageOptions,
  pageCount: controlledPageCount,
  initialPageSize = 5,
  rowLevelRightClickMenus = [],
  initialGlobalFilter,
  stickyHeader = false,
  stickyHeaderConfig = {},
  isGlobalFilterEnabled,
  tableNameString,
  enableManualFilter = false,
  enableControlledPagination = false,
  enableManualSort = false,
  fetchData,
  dataLength,
  skipPageReset,
  searchTerm,
  onSortChange,
}) => {
  const theme = useTheme();
  const { show } = useContextMenu({
    id: 'react-mui-table-list-view-row-level-menu',
  });
  const menuId = `react-mui-table-list-view-row-level-menu-${Date.now()}`;

  const newTheme = createTheme(
    _.merge(_.cloneDeep(theme), externalCustomTheme)
  );

  /**
   * @function checkBoxCell
   * @description checkBoxCell - this function renders the cell for checkbox
   * @returns {JSX}
   */
  const checkBoxCell = ({ row }) => {
    return (
      <div>
        <IndeterminateCheckbox
          {...row.getToggleRowSelectedProps({
            // eslint-disable-next-line no-use-before-define
            onChange: toggleRowSelection.bind(null, row),
          })}
        />
      </div>
    );
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    gotoPage,
    setPageSize,
    prepareRow,
    toggleAllRowsSelected,
    getToggleAllRowsSelectedHandler,
    setGlobalFilter,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter, sortBy },
  } = useTable(
    {
      columns,
      data,
      autoResetSelectedRows: false,
      autoResetPage: !skipPageReset,
      initialState: {
        pageIndex: 0,
        pageSize: initialPageSize,
        globalFilter: initialGlobalFilter ?? '',
      },
      pageCount: controlledPageCount,
      manualPagination: enableControlledPagination,
      manualGlobalFilter: enableManualFilter,
      manualSortBy: enableManualSort,
    },

    useGlobalFilter,
    useFlexLayout,
    useResizeColumns,
    useColumnOrder,
    useGroupBy,

    useSortBy,
    useExpanded,
    usePagination,

    useRowSelect,

    (hooks) => {
      if (
        rowSelectionConfig?.enable &&
        rowSelectionConfig?.mode === 'checkbox'
      ) {
        hooks.allColumns.push((col) => [
          {
            id: 'selection',
            Header: (
              <RowSelectionHeader rowSelectionConfig={rowSelectionConfig} />
            ),
            width: 100,
            Cell: checkBoxCell,
          },
          ...col,
        ]);
      }
    }
  );

  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortBy);
    }
  }, [sortBy, onSortChange]);

  useEffect(() => {
    if (onTableStateChange) {
      onTableStateChange({
        pageIndex,
        pageSize,
        selectedRowIds,
        getToggleAllRowsSelectedHandler,
        toggleAllRowsSelected,
        globalFilter,
      });
    }
  }, [selectedRowIds, pageIndex, pageSize, globalFilter]);
  useEffect(() => {
    gotoPage(0);
  }, [searchTerm]);

  // globalFilter effect should only be considered when controlled filtering is true else not.
  useEffect(() => {
    const fetchDataWithGlobalFilter = () => {
      if (enableControlledPagination && fetchData) {
        fetchData(pageIndex, pageSize, globalFilter);
      }
    };

    const fetchDataWithoutGlobalFilter = () => {
      if (enableControlledPagination && fetchData) {
        fetchData(pageIndex, pageSize);
      }
    };

    if (enableManualFilter) {
      fetchDataWithGlobalFilter();
    } else {
      fetchDataWithoutGlobalFilter();
    }
  }, [
    enableManualFilter,
    enableControlledPagination,
    fetchData,
    pageIndex,
    pageSize,
    globalFilter,
  ]);

  const toggleRow = () => {
    toggleAllRowsSelected(false);
  };
  useEffect(() => {
    toggleRow();
  }, [toggle]);

  /**
   * @function handleChangePage
   * @description function to handle page change
   * @param {object} event
   * @param {number} newPage
   * @returns {void}
   */
  const handleChangePage = useCallback(
    (_event, newPage) => {
      gotoPage(newPage);
    },
    [gotoPage]
  );
  const allOptionValueSetter = (rowsPerPageOptionsParam, dataLengthParam) => {
    if (
      rowsPerPageOptionsParam &&
      [...rowsPerPageOptionsParam, 5, 10, 20, 50].includes(
        Number(dataLengthParam)
      )
    ) {
      return Number(dataLengthParam) + 1;
    }
    if ([5, 10, 20, 50].includes(Number(dataLengthParam))) {
      return Number(dataLengthParam) + 1;
    }
    return dataLengthParam;
  };

  /**
   * @function handleChangeRowsPerPage
   * @description function to handle rows per page change
   * @param {object} event
   * @returns {void}
   */

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      setPageSize(Number(event.target.value));
      gotoPage(0);
    },
    [setPageSize, gotoPage]
  );
  const toggleRowSelection = useCallback(
    (row) => {
      if (rowSelectionConfig?.enable) {
        if (rowSelectionConfig?.type === 'single-select' && !row.isSelected) {
          toggleAllRowsSelected(false);
        }
        row.toggleRowSelected();
      }
    },
    [rowSelectionConfig, toggleAllRowsSelected]
  );

  const onChangeHandler = useCallback(
    (e) => {
      setGlobalFilter(e.target.value ?? undefined);
    },
    [setGlobalFilter]
  );

  useEffect(() => {
    const handleResetPageIndex = () => {
      gotoPage(0); // Set the page index to zero
    };

    // Add an event listener for the custom event
    window.addEventListener('RESET_PAGE_INDEX', handleResetPageIndex);

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener('RESET_PAGE_INDEX', handleResetPageIndex);
    };
  }, []);

  return (
    <>
      {' '}
      <ThemeProvider theme={newTheme}>
        {isGlobalFilterEnabled === true && (
          <Grid conatiner>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  flexGrow={1}
                  flexDirection="row"
                  alignItems="center"
                >
                  <Typography
                    variant="h3"
                    component="h3"
                    sx={{
                      fontStyle: 'normal',
                      border: 'none',
                      '&:focus': commonFocusStyle,
                    }}
                    tabIndex={0}
                    aria-label={tableNameString}
                  >
                    {tableNameString}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" mt="10px" width="200px">
                  <SearchBox
                    value={globalFilter ?? ''}
                    onChange={onChangeHandler}
                    placeholder="Search"
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
        <TableContainer
          style={
            stickyHeader
              ? { maxHeight: 250, ...stickyHeaderConfig.containerStyle }
              : {}
          }
          className="list-table-container"
        >
          <Table
            {...getTableProps()}
            size="medium"
            stickyHeader={stickyHeader}
            className="list-table"
          >
            <TableHead
              style={
                stickyHeader
                  ? {
                      position: 'sticky',
                      top: 0,
                      ...stickyHeaderConfig.tableHeadStyle,
                      zIndex: 1,
                    }
                  : {}
              }
            >
              {headerGroups.map((headerGroup) => (
                <TableRow
                  key={JSON.stringify(headerGroup)}
                  {...headerGroup.getHeaderGroupProps()}
                  role="row"
                  scope="row"
                >
                  {headerGroup.headers.map((column, key) => (
                    <TableCell
                      // eslint-disable-next-line react/no-array-index-key
                      key={key}
                      align="center"
                      variant="head"
                      role="columnheader"
                      scope="col"
                      tabIndex={0}
                      aria-label={column?.heading}
                      title={column?.heading}
                      className="list-table-header"
                      sx={{
                        '&:focus-visible': commonFocusStyle,
                      }}
                      {...column.getHeaderProps([
                        getColumnProps(),
                        getHeaderProps(),
                      ])}
                    >
                      {column.render('Header')}

                      {!['selection', 'expander', 'pk', ''].includes(
                        column.id
                      ) ? (
                        <Box
                          // @ts-ignore
                          {...(column.id === 'selection'
                            ? {}
                            : column.getSortByToggleProps())}
                        >
                          {column.isSorted ? (
                            <IconButton
                              tabIndex={0}
                              sx={{
                                marginLeft: '-4px',
                                width: '12px',
                                height: '12px',
                                '&:focus-visible': {
                                  outline: 'none',
                                  border: `2px solid ${theme?.palette?.primary?.main} !important`,
                                },
                              }}
                            >
                              <TableSortLabel
                                aria-label={`Sorted ${
                                  column?.isSortedDesc
                                    ? 'descending'
                                    : 'ascending'
                                }`}
                                aria-sort={
                                  column.isSortedDesc
                                    ? 'descending'
                                    : 'ascending'
                                }
                                component="span"
                                active={column?.isSorted}
                                direction={
                                  column?.isSortedDesc ? 'desc' : 'asc'
                                }
                                sx={{
                                  '& .MuiTableSortLabel-icon': {
                                    color: `${theme?.palette?.primary?.main} !important`,
                                  },
                                }}
                              />
                            </IconButton>
                          ) : !column.disableSortBy ? (
                            <SortButtonArrowIcon
                              height={12}
                              width={12}
                              style={{
                                transform: 'translate(0, 2px)',
                              }}
                            />
                          ) : null}
                        </Box>
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>

            <TableBody {...getTableBodyProps()} className="list-table-body">
              {(enablePagination ? page : rows)?.map((row, i) => {
                prepareRow(row);
                return (
                  <TableRow
                    key={row?.id || i}
                    {...row.getRowProps([
                      {
                        ...getRowProps(row, theme, toggleAllRowsSelected),
                        onContextMenu: (e) => {
                          show({
                            id: menuId,
                            event: e,
                            props: { ...row },
                          });
                        },
                      },
                    ])}
                    sx={{
                      '&:hover': {
                        [`& .row-level-options-on-hover-${i}`]: {
                          display: 'block',
                        },
                        backgroundColor: (_theme) =>
                          `${_theme?.palette?.other?.listTableRowSelectedBg} !important`,
                      },
                      '&:focus-visible': {
                        backgroundColor: (_theme) =>
                          `${_theme?.palette?.other?.listTableRowSelectedBg} !important`,
                      },
                    }}
                  >
                    {!enableRowSelection
                      ? rowLevelOnHoverOptions({
                          row,
                          rowIndex: row?.id || i,
                          className: `row-level-options-on-hover-${row?.id || i}`,
                          containerSx: ROW_LEVEL_HOVER_OPTIONS_DEFAULT_STYLE,
                        })
                      : ''}
                    {row.cells.map((cell, index) => {
                      return (
                        <TableCell
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          className="list-table-cell"
                          {...cell.getCellProps([
                            {
                              style: {
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                wordBreak: 'initial',
                                whiteSpace: 'nowrap',
                                height: '50px',
                              },
                              onClick: (e) => e.stopPropagation(),
                            },
                          ])}
                        >
                          {cell.render('Cell')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box width="100%" overflow="auto">
          {enablePagination && (
            <TablePagination
              component="div"
              rowsPerPageOptions={[
                ...(rowsPerPageOptions
                  ? rowsPerPageOptions.map((ele) => {
                      return { label: `${ele} rows`, value: ele };
                    })
                  : [5, 10, 20, 50].map((ele) => {
                      return { label: `${ele} rows`, value: ele };
                    })),
                ...(isAllOptionDisabled
                  ? []
                  : [
                      {
                        label: 'All',
                        value: enableControlledPagination
                          ? allOptionValueSetter(rowsPerPageOptions, dataLength)
                          : allOptionValueSetter(
                              rowsPerPageOptions,
                              data.length
                            ),
                      },
                    ]),
              ]}
              colSpan={columns?.length}
              count={enableControlledPagination ? dataLength : rows.length}
              rowsPerPage={pageSize}
              page={pageIndex}
              slotProps={{
                select: {
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Show:"
              ActionsComponent={ReactMuiTablePaginationActions}
            />
          )}
        </Box>
        {rowLevelRightClickMenus?.length ? (
          <ContextMenus
            menus={rowLevelRightClickMenus}
            additionalData={{ test: '1' }}
            menuId={menuId}
          />
        ) : null}
      </ThemeProvider>
    </>
  );
};

ReactMuiTableListView.defaultProps = {
  getHeaderProps: () => ({}),
  getColumnProps: () => ({}),
  getRowProps: () => ({}),
  rowLevelOnHoverOptions: () => ({}),
  onRowSelect: () => ({}),
  externalCustomTheme: {},
  rowSelectionConfig: {
    enable: false,
    mode: 'checkbox',
    showDeleteOnSelected: false,
    type: 'multi-select',
    showSelectAllLabel: true,
  },
  onTableStateChange: () => ({}),
  enableRowSelection: false,
  toggle: false,
  rowLevelRightClickMenus: [],
  stickyHeaderConfig: {},
  stickyHeader: false,
  isGlobalFilterEnabled: false,
  enableControlledPagination: false,
  enableManualFilter: false,
  fetchData: () => {},
};

ReactMuiTableListView.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  getHeaderProps: PropTypes.func,
  getColumnProps: PropTypes.func,
  getRowProps: PropTypes.func,
  rowLevelOnHoverOptions: PropTypes.func,
  onTableStateChange: PropTypes.func,
  onRowSelect: PropTypes.func,
  enableRowSelection: PropTypes.bool,
  toggle: PropTypes.bool,
  enablePagination: PropTypes.bool.isRequired,
  initialPageSize: PropTypes.number.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  pageCount: PropTypes.number.isRequired,
  isAllOptionDisabled: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string.isRequired,
  externalCustomTheme: PropTypes.oneOfType([PropTypes.object]),
  rowSelectionConfig: PropTypes.oneOfType([PropTypes.object]),
  rowLevelRightClickMenus: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object])
  ),
  initialGlobalFilter: PropTypes.string.isRequired,
  enableControlledPagination: PropTypes.bool,
  enableManualFilter: PropTypes.bool,
  fetchData: PropTypes.func,
  dataLength: PropTypes.number.isRequired,
  skipPageReset: PropTypes.bool.isRequired,
  tableNameString: PropTypes.string.isRequired,
  stickyHeaderConfig: PropTypes.oneOfType([PropTypes.object]),
  stickyHeader: PropTypes.bool,
  isGlobalFilterEnabled: PropTypes.bool,
};

export default ReactMuiTableListView;
